import { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { periodWindow, type TargetWindow } from '@/lib/targets';
import type { TargetWindowChoice } from '@/lib/use-my-targets';
import type {
  TargetActual,
  TargetActualsParams,
  TargetActualsRegion,
  TargetActualsSubject,
  TargetAttribution,
  TargetBillingRegion,
  TargetFxMode,
  TargetMetric,
  TargetPeriodRecord,
  TargetTranslation,
  User,
  UserGroup,
} from '@/lib/types';

/**
 * THE MANAGEMENT DASHBOARD'S READS.
 *
 * Four hooks, deliberately separate rather than one that fetches everything:
 * the period list is read once per mount, the team's figures once per window,
 * the member list once per team, and the members' figures once per (team,
 * window). Merging them would refetch the period list on every click of the
 * window picker — a list that cannot have changed since the mount.
 * ⚠️ *SUPERSEDED: "against a 120-per-60s per-IP budget shared across the
 * office."* It is **12,000/60s** (corrected 2026-09-17). Splitting the four is
 * still right — re-reading a list that did not move is waste at any ceiling —
 * but the budget that reason cited is off by a factor of a hundred.
 *
 * ── ⚠️ THE 403 IS THE THING THIS FILE EXISTS TO GET RIGHT ───────────────────
 *
 * `GET /targets/actuals` refuses `subjectType: 'user'` naming somebody other
 * than the caller unless they hold the targets audit
 * (`targets.service.ts:1143`). It is a REFUSAL, not a narrowed list.
 *
 * 🔴 **NEVER CATCH THAT 403 AND RETRY WITHOUT `subjectId`.** The retry succeeds
 * — the subject defaults to `organisation` server-side — and hands back the
 * WHOLE BUSINESS'S figures, which this page would then render under one
 * person's name, in a table of their colleagues, with nothing on screen looking
 * wrong. There is no retry anywhere in this file and there must never be one.
 *
 * ⚠️ **A refusal is carried as its own state, never folded into `error`.** *You
 * may not ask* and *we could not find out* send a reader to different places —
 * one to an administrator, one to a retry — and a 403 painted in the danger
 * register sends somebody to fix a system that is working as configured.
 *
 * ⚠️ **`targets` IS NOT IN `ModuleSlug`, so the grant cannot be pre-checked.**
 * There is no `targets.read` scope in `/permissions/me` this app models, and
 * inventing a slug would make the page answer a question nobody has put to the
 * server. The 403 where it arrives is the whole mechanism, which is why every
 * member row can carry one independently.
 *
 * ── WHO CAN SEE WHAT, MEASURED ─────────────────────────────────────────────
 *
 *   organisation subject   any holder of `targets.read`. No refusal.
 *   user_group subject     any holder of `targets.read`. No refusal — the owner
 *                          ruled a group target names nobody in particular
 *                          (2026-08-31), and the guard tests `=== 'user'`
 *                          positively so a group can never inherit it.
 *   user subject           yourself always; anybody else needs the audit.
 *
 * ⚠️ **So the holistic half of this page works for everyone and the per-person
 * half does not.** A manager who is not an administrator sees the team's figures
 * and a refusal where the breakdown would be. That is the shape as built — see
 * the page, which says so — and **there is no client-side "my reports" view to
 * add**: nothing server-side knows who reports to whom, so it would be a
 * convention presented as access control.
 */

/* -------------------------------------------------------------------------
 * Which team
 * --------------------------------------------------------------------- */

/**
 * Whose figures the Team tab is showing.
 *
 * ⚠️ **A discriminated union rather than a nullable group id.** The organisation
 * and a group are different QUERIES, not one query with a filter left off — and
 * `unattributed` is reported for the first and withheld for the second, so a
 * caller that could not tell them apart would render a real number as an absence
 * or an absence as a zero.
 */
export type TeamSubject =
  | { kind: 'organisation' }
  | { kind: 'group'; groupId: number; slug: string; name: string };

export function teamSubjectKey(subject: TeamSubject): string {
  return subject.kind === 'organisation' ? 'org' : `group:${subject.groupId}`;
}


/* -------------------------------------------------------------------------
 * The periods, and the one covering today
 * --------------------------------------------------------------------- */

/** Bare `YYYY-MM-DD` from LOCAL parts — `toISOString()` is the same UTC bug mirrored. */
function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * The period this dashboard DEFAULTS to: the WIDEST one covering today.
 *
 * ⚠️ **Periods legitimately overlap** — a quarter sits inside a year and both
 * are real — which is exactly why the endpoint refuses to guess and requires a
 * window. Something still has to choose a default, so this takes the widest: an
 * annual figure is what a dashboard headline answers, and anything narrower is a
 * narrowing somebody asks for. The choice is not hidden — the window is spelled
 * out beside the figures.
 *
 * ⚠️ **This is the same rule `use-my-targets.ts` applies to the partnerships
 * page, and the two are separate copies.** Not ideal, and deliberately not
 * unified in this change: that module's copy is private to a hook another page
 * mounts, and moving it is churn in a file this change has no business editing.
 * **If either moves, move both** — two dashboards disagreeing about which period
 * is "now" would put two different windows on screen under one word.
 *
 * ⚠️ **THE PAIR WAS KEPT IN STEP ON 2026-09-13 AND THE CYCLE CONTROL IS WHY IT
 * MATTERED.** Both hooks now expose the whole period LIST beside this reduction —
 * `TargetPeriodsState.periods` here, `MyTargets.periods` there — with the same
 * `?isActive=true&perPage=100` read, the same absence of a `kind` filter, and the
 * list carried through every settled branch including the one that reports no
 * period. **So there are now THREE things to move together, not one**: this
 * function, the exposed list, and the `perPage` ceiling behind it.
 *
 * ⛔ **ONE THING WAS DELIBERATELY *NOT* KEPT IN STEP, and it is recorded rather
 * than quietly evened up:** `use-my-targets.ts` re-derived its `noPeriod` from
 * `asked === null` and this hook did not. That is not drift — that hook sees the
 * caller's `TargetWindowChoice` and this one takes no arguments at all, so it
 * cannot ask the better question. See the note on `noPeriod`.
 */
function periodForToday(periods: readonly TargetPeriodRecord[]): TargetPeriodRecord | null {
  const today = todayIso();
  const covering = periods.filter((p) => p.startDate <= today && today <= p.endDate);
  if (covering.length === 0) return null;
  return covering.reduce((widest, p) =>
    p.startDate < widest.startDate ||
    (p.startDate === widest.startDate && p.endDate > widest.endDate)
      ? p
      : widest,
  );
}

export interface TargetPeriodsState {
  period: TargetPeriodRecord | null;
  /**
   * 🔴 **EVERY CATALOGUED ACTIVE PERIOD — the list `TargetWindowPicker`'s CYCLE
   * control offers, new 2026-09-13.**
   *
   * ⚠️ **THE LIST WAS ALWAYS READ AND WAS NEVER EXPOSED, WHICH IS WHY STEP ONE OF
   * THAT PICKER WAS MISSING RATHER THAN BADLY BUILT.** The effect below has
   * fetched `?isActive=true&perPage=100` since this hook was written and then
   * collapsed it to ONE record through `periodForToday`, so a caller could see the
   * period it defaulted to and had no way to name another.
   *
   * ⚠️ **`MyTargets.periods` is the twin of this and carries the same notes** —
   * they are two copies, like `periodForToday` itself. **If either moves, move
   * both**; two dashboards offering different cycles under one word is the same
   * failure as two disagreeing about which period is "now".
   *
   * ⚠️ **NOT filtered by `kind`, deliberately** — the picker wants `kind: 'cycle'`
   * and filters for itself. A hook narrowing on a caller's behalf would make the
   * row the picker pins back — a `year` period covering today, an ordinary default
   * — unreachable from the only place that can see it is missing.
   *
   * ⚠️ **It is returned in EVERY settled branch, `noPeriod` included**, which is
   * not tidiness: that branch is the one a reader escapes by choosing a cycle, so
   * the list has to survive the sentence telling them there is no period.
   *
   * Empty while loading, on error, and where nothing is catalogued.
   */
  periods: readonly TargetPeriodRecord[];
  loading: boolean;
  error: string | null;
  /**
   * No catalogued period contains today, so there is nothing to DEFAULT to.
   *
   * 🔴 **IT IS A STATEMENT ABOUT THE DEFAULT AND NOT ABOUT THE PANEL — and since
   * the cycle control shipped on 2026-09-13 a caller must not blank on it
   * alone.** A reader may CHOOSE a cycle that does not cover today (a catalogued
   * set adrift of the calendar is ordinary), and that choice resolves to a real
   * window through `resolveWindow` while this flag stays `true`. **Test
   * `resolveWindow(choice, periodState.period) === null` instead** — that is the
   * one question "is there anything to report on" has, and it accounts for the
   * chosen cycle, the default and a half-typed range at once.
   *
   * ⚠️ **This hook CANNOT answer the better question and must not pretend to**: it
   * takes no arguments and has never seen the choice. `use-my-targets.ts` sees
   * both, which is why its own twin of this flag WAS re-derived — it tests
   * `asked === null` now, not `period === null`. **The two hooks differ here for a
   * reason, not by drift.**
   */
  noPeriod: boolean;
}

export function useDefaultTargetPeriod(): TargetPeriodsState {
  const [state, setState] = useState<{
    period: TargetPeriodRecord | null;
    /**
     * ⚠️ **THE WHOLE LIST IS KEPT BESIDE THE DEFAULT, not derived from it.**
     * `periodForToday` is a REDUCTION — the widest period covering today — so the
     * list cannot be recovered from its answer, and the cycle control needs the
     * list. Two slices of one response, so they cannot disagree about which read
     * they came from.
     */
    periods: TargetPeriodRecord[];
    error: string | null;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listTargetPeriods({ isActive: true, perPage: 100 }, signal)
      .then((page) => {
        if (signal.aborted) return;
        setState({ period: periodForToday(page.data), periods: page.data, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setState({
          period: null,
          periods: [],
          error: caught instanceof Error ? caught.message : 'Could not read the target periods.',
        });
      });

    return () => controller.abort();
  }, []);

  if (state === null) {
    return { period: null, periods: [], loading: true, error: null, noPeriod: false };
  }
  if (state.error !== null) {
    return { period: null, periods: [], loading: false, error: state.error, noPeriod: false };
  }
  return {
    period: state.period,
    /*
     * ⚠️ **Carried even when `noPeriod` is true**, which is the branch it matters
     * most in: no period covers today, so the cycle control is the reader's only
     * way to name one, and a list emptied by the refusal would leave them with no
     * exit. See the note on `noPeriod`.
     */
    periods: state.periods,
    loading: false,
    error: null,
    noPeriod: state.period === null,
  };
}

/* -------------------------------------------------------------------------
 * One subject's figures over one window
 * --------------------------------------------------------------------- */

export interface ActualsState {
  byMetric: Partial<Record<TargetMetric, TargetActual>>;
  attribution: TargetAttribution | null;
  /** Organisation queries only; `null` everywhere else, and that is not a zero. */
  unattributed: string | null;
  /** The window the SERVER says it measured, falling back to the one asked for. */
  window: TargetWindow | null;
  /**
   * 🔴 **THE CURRENCY THE SERVER ACTUALLY SUMMED IN. LABEL EVERY AMOUNT FROM
   * THIS, NEVER FROM A METRIC'S `baseCurrency`** — that one is the stored
   * TARGET's currency and is a different fact, `null` on a count and on any
   * metric nobody set a target for.
   *
   * ⚠️ **`null` means the server did not say**, which is an older API that
   * predates the `baseCurrency` parameter — and it summed in GBP, because that
   * was the only thing it could do. A caller must render that case rather than
   * assume its request was honoured: asking for USD and being answered in GBP
   * with a USD label is the failure this field exists to make impossible.
   */
  measuredInCurrency: string | null;
  /**
   * 🔴 **THE BILLING REGION THE SERVER SAYS IT NARROWED TO. LABEL THE SCOPE FROM
   * THIS, NEVER FROM THE PICKER'S STATE.**
   *
   * ⚠️ **`null` means "the whole population", by one of two routes** — no region
   * was asked for, or the server predates the parameter and could not narrow. A
   * caller that asked for a band and reads `null` back has NOT been refused and
   * has NOT got its narrowing: the figures are real, correct and broader than
   * the heading it was about to print. **Say so rather than printing the band.**
   *
   * ⚠️ **Asking for Europe and being answered with the world under a Europe
   * heading is the failure this field exists to make impossible** — the exact
   * shape `measuredInCurrency` guards for the unit, one field up.
   */
  measuredForBillingRegion: TargetBillingRegion | null;
  /**
   * The FX mode the SERVER echoed (owner 2026-10-28); absent on the wire = an
   * older API = `'document'`, and `'document'` on every non-figures state.
   */
  fxMode: TargetFxMode;
  /** The `period_end` translation rate; `null` in document mode and when not sent. */
  translation: TargetTranslation | null;
  /**
   * The exact `/targets/actuals` query these figures were measured with, or
   * `null` when nothing was measured successfully. The tile drill-down (owner
   * 2026-10-28) reuses it with `metric` added, so its rows match the tile.
   */
  actualsQuery: TargetActualsParams | null;
  loading: boolean;
  error: string | null;
  /** A 403. Its own state — see the module comment. */
  refused: string | null;
}

const NO_ACTUALS: ActualsState = {
  byMetric: {},
  attribution: null,
  unattributed: null,
  window: null,
  measuredInCurrency: null,
  measuredForBillingRegion: null,
  fxMode: 'document',
  translation: null,
  actualsQuery: null,
  loading: false,
  error: null,
  refused: null,
};

/** Identity of a window as far as one request is concerned. */
function windowKey(window: TargetWindow | null): string {
  if (window === null) return '';
  return `${window.periodId ?? ''}|${window.from}|${window.to}`;
}

/**
 * ⚠️ **The window a request ASKED for is the fallback label, never the answer
 * where there is one.** The server echoes what it actually measured, and that
 * echo wins: a figure read under a label describing a different span is the one
 * failure this whole feature is arranged to prevent. An older server sends no
 * echo, and then the requested window is exactly right.
 */
function measuredWindow(asked: TargetWindow, echoed: { from?: string; to?: string }): TargetWindow {
  const { from, to } = echoed;
  if (from === undefined || to === undefined) return asked;
  if (from === asked.from && to === asked.to) return asked;
  return { name: 'Custom range', from, to, periodId: asked.periodId };
}

/**
 * The window a choice resolves to, or `null` when the caller has not finished
 * asking.
 *
 * 🔴 **SINCE 2026-09-13 THIS IS ALSO THE ONLY HONEST ANSWER TO "IS THERE ANYTHING
 * TO REPORT ON", AND `TargetPeriodsState.noPeriod` IS NOT.** A reader who has
 * chosen a cycle at step one of `TargetWindowPicker` hands over
 * `{kind: 'window'}` carrying that cycle's own window, so this returns a real
 * window **while `noPeriod` stays `true`** wherever no catalogued period happens
 * to cover today. Testing the flag alone blanks a panel over a window somebody
 * deliberately named.
 *
 * ⚠️ **`period` is the caller's DEFAULT and is consulted only for
 * `{kind: 'period'}`** — an untouched picker. A chosen cycle never reaches this
 * argument, which is exactly why the flag and this function can disagree.
 *
 * ⚠️ **It needs no change for step one, and that is by construction rather than
 * luck**: the cycle arrives already resolved to a `TargetWindow` carrying its own
 * `periodId`, so every consumer that keys on a window — both hooks' cache keys
 * included — picks the change up with no new term. See `windowChoiceOf`.
 */
export function resolveWindow(
  choice: TargetWindowChoice,
  period: TargetPeriodRecord | null,
): TargetWindow | null {
  if (choice.kind === 'none') return null;
  if (choice.kind === 'window') return choice.window;
  return period === null ? null : periodWindow(period);
}

/**
 * Whose figures — the subject half of an actuals query.
 *
 * ⚠️ **Mirrors `TargetActualsSubject` as three POSITIVE arms**, which is what
 * makes a wrong combination a compile error rather than a 422: `subjectGroupId`
 * is required on a group query and refused on the other two, and `subjectId` is
 * required on a user query and refused on a group one.
 */
export type ActualsSubject =
  | { subjectType: 'organisation' }
  | { subjectType: 'user_group'; subjectGroupId: number }
  | { subjectType: 'user'; subjectId: number };

/**
 * One subject's five metrics over one window.
 *
 * `subject` is the SUBJECT HALF of the query, so this hook serves the team tab
 * (organisation / group) and the individual tab (user) without branching on
 * which page mounted it.
 *
 * ⚠️ **The subject is destructured into PRIMITIVE dependencies.** Depending on
 * the object would refetch on every render — a caller builds a fresh one each
 * time and nothing memoises it — and a `JSON.stringify` dependency would work by
 * accident and break the day a key order changed.
 *
 * ⚠️ **The answer is stamped with BOTH the subject and the window.** Figures for
 * the subject just left must read as absent rather than appear under the new
 * subject's name, and the same for the window. Two stamps, because they change
 * independently.
 */
export function useTargetActuals(
  subject: ActualsSubject,
  window: TargetWindow | null,
  enabled = true,
  /**
   * 🔴 **WHICH CURRENCY TO SUM IN, AND IT IS PART OF THE CACHE KEY BELOW.**
   *
   * Omitted, the server sums in GBP — exactly what it did before the parameter
   * existed, so every caller that ignores this is unchanged.
   *
   * ⚠️ **It MUST join `key`.** Without that, switching currency leaves the
   * cached figures on screen while the new code is printed beside them: a USD
   * dashboard showing GBP amounts labelled USD. A wrong number wearing a
   * plausible unit is worse than the understatement this parameter exists to
   * fix, because nothing about it looks wrong.
   */
  baseCurrency?: string,
  /**
   * 🔴 **WHICH BILLING REGION TO NARROW TO, AND IT IS PART OF THE CACHE KEY
   * BELOW.**
   *
   * A team target is set per region, so the figure it is read against has to be
   * narrowed the same way — a Europe commitment measured against the whole
   * world's revenue is a number that is right about nothing. Omitted, the server
   * sums everything, which is exactly what it did before the parameter existed.
   *
   * ⚠️ **It MUST join `key`, and its failure is quieter than the currency's.** A
   * region change moves neither the subject nor the window, so keyed without it
   * the cached figures survive untouched and are relabelled with the new band.
   * A mislabelled currency at least prints a unit; a mislabelled scope prints a
   * plausible figure with nothing beside it to doubt.
   *
   * ⚠️ **`'none'` is a real narrowing — the unbanded companies — and is NOT the
   * same as omitting the key.** The key segment is `none` for one and empty for
   * the other, so the two can never share an answer.
   *
   * ⚠️ **`null` and `undefined` both mean the whole population** and are folded
   * at the query builder rather than at every call site — a region control
   * holds `null` for "All regions", a caller without one passes nothing, and
   * neither reaches the wire. ⛔ **Neither is `'none'`.**
   */
  billingRegion?: TargetBillingRegion | null,
  /**
   * 🔴 **HOW FOREIGN AMOUNTS ARE CONVERTED (owner 2026-10-28), AND IT JOINS
   * `key`.** `'document'` (default) = each document at its own date and the key
   * is not sent; `'period_end'` = Xero's P&L translation at one rate.
   */
  fxMode: TargetFxMode = 'document',
): ActualsState {
  const subjectType = subject.subjectType;
  const subjectId = subject.subjectType === 'user' ? subject.subjectId : null;
  const subjectGroupId = subject.subjectType === 'user_group' ? subject.subjectGroupId : null;
  const subjectKey = `${subjectType}:${subjectId ?? ''}:${subjectGroupId ?? ''}`;
  const [state, setState] = useState<{
    key: string;
    byMetric: Partial<Record<TargetMetric, TargetActual>>;
    attribution: TargetAttribution | null;
    unattributed: string | null;
    window: TargetWindow;
    measuredInCurrency: string | null;
    measuredForBillingRegion: TargetBillingRegion | null;
    fxMode: TargetFxMode;
    translation: TargetTranslation | null;
    actualsQuery: TargetActualsParams | null;
    error: string | null;
    refused: string | null;
  } | null>(null);

  const wKey = windowKey(window);
  /*
   * 🔴 **FOUR STAMPS: subject, window, currency, REGION — and they change
   * independently, which is the whole reason each is its own segment.** A region
   * moves while the other three stand still, so an answer keyed on the first
   * three outlives it and is redrawn under the new band's name: the whole
   * population's revenue, correctly dated, correctly denominated, correctly
   * attributed, and scoped to something else entirely.
   *
   * ⚠️ **`undefined` and `'none'` are deliberately different keys** — `''`
   * against `none`. Omitting the key asks for everything; `'none'` asks for the
   * companies nobody has banded, which is a real and much smaller population.
   */
  const key = `${subjectKey}#${wKey}#${baseCurrency ?? ''}#${billingRegion ?? ''}#${fxMode}`;
  const askedName = window?.name ?? '';
  const askedFrom = window?.from ?? '';
  const askedTo = window?.to ?? '';
  const askedPeriodId = window?.periodId ?? null;

  useEffect(() => {
    if (!enabled || wKey === '') return;
    const controller = new AbortController();
    const { signal } = controller;

    const askedWindow: TargetWindow = {
      name: askedName,
      from: askedFrom,
      to: askedTo,
      periodId: askedPeriodId,
    };
    /*
     * A window carrying an id is asked for BY id: sending its dates instead is a
     * different question that happens to share an answer today and stops sharing
     * one the moment a period's boundary is corrected.
     */
    const windowQuery =
      askedPeriodId === null
        ? { from: askedFrom, to: askedTo }
        : { periodId: askedPeriodId };

    /*
     * Rebuilt from the primitives rather than closed over, so every value this
     * effect reads is also a value it depends on. The three arms are written out
     * because the query type is a union of unions and a spread of one loses the
     * discrimination — which is the whole reason the type is shaped that way.
     */
    const subjectPart: TargetActualsSubject =
      subjectType === 'user'
        ? { subjectType: 'user', subjectId: subjectId ?? 0 }
        : subjectType === 'user_group'
          ? { subjectType: 'user_group', subjectGroupId: subjectGroupId ?? 0 }
          : { subjectType: 'organisation' };

    /*
     * ⚠️ The key is SPREAD CONDITIONALLY rather than sent as `undefined`. Every
     * write schema on this API is `.strict()`, and `listQuery` serialises what
     * it is given — an explicit `undefined` is a key the server may refuse,
     * where an absent one is the documented default of GBP.
     */
    /*
     * 🔴 **ANNOTATED, AND THAT IS NOT DECORATION — IT IS THE ONLY THING
     * CHECKING THIS KEY EXISTS.** The object below ends in `as
     * TargetActualsParams`, and a cast turns excess-property checking off
     * outright; a conditional spread is exempt from it in the first place. So
     * `billingRegion` could be spelled anything at all here, compile green,
     * serialise happily and reach a `.strict()` schema as an unrecognised key —
     * which this repo has already shipped once, on `/revenue-tracker`, on this
     * same key name. The annotation forces the property against
     * `TargetActualsRegion`, so a name that is not on the interface fails HERE
     * rather than on the wire.
     */
    const regionQuery: TargetActualsRegion =
      billingRegion === undefined || billingRegion === null ? {} : { billingRegion };

    const query = {
      ...subjectPart,
      ...windowQuery,
      ...(baseCurrency === undefined ? {} : { baseCurrency }),
      ...regionQuery,
      // Absent = document mode (owner 2026-10-28); only `period_end` is sent.
      ...(fxMode === 'document' ? {} : { fxMode }),
    } as TargetActualsParams;

    api
      .targetActuals(query, signal)
      .then((answer) => {
        if (signal.aborted) return;
        /*
         * ⚠️ **THE WHOLE ROW IS STORED, DELIBERATELY.** `excludedByCurrency` was
         * on the wire for weeks and reached no screen because it was in no type
         * and therefore in nobody's loader, and nothing went red about it.
         * Keeping the row whole means a field the server adds is carried by
         * construction.
         */
        const byMetric: Partial<Record<TargetMetric, TargetActual>> = {};
        for (const row of answer.data.metrics) byMetric[row.metric] = row;
        setState({
          key,
          byMetric,
          attribution: answer.data.attribution,
          /*
           * ⚠️ Taken off the FIRST metric row, and it is the same value on all
           * five by construction — the server computes it once per subject.
           * `?? null` here is not a coalesce of a figure: the key is absent
           * entirely on a server predating it, and absent and null both mean
           * "not reported", which is genuinely not a number.
           */
          unattributed: answer.data.metrics[0]?.unattributed ?? null,
          window: measuredWindow(askedWindow, answer.data),
          // Absent on a server predating the parameter; see ActualsState.
          measuredInCurrency: answer.data.measuredInCurrency ?? null,
          /*
           * ⚠️ **THE SERVER'S ANSWER, NOT THE REQUEST ECHOED BACK.** Recording
           * `billingRegion` here would turn "we asked" into "it was honoured" —
           * the proxy-field shape — and on a server that ignores the key it
           * would name a band the figure was never narrowed to.
           */
          measuredForBillingRegion: answer.data.billingRegion ?? null,
          // The server's echo; absent = an older API = document mode.
          fxMode: answer.data.fxMode ?? 'document',
          translation: answer.data.translation ?? null,
          actualsQuery: query,
          error: null,
          refused: null,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * 🔴 **THE REFUSAL IS RECORDED AND THE REQUEST IS NOT REPEATED.**
         * Retrying without `subjectId` succeeds and returns the caller's own
         * figures under somebody else's name. See the module comment.
         *
         * A 403 carries no `details`, so the message is the whole content.
         */
        const refused = caught instanceof ApiError && caught.status === 403;
        setState({
          key,
          byMetric: {},
          attribution: null,
          unattributed: null,
          window: askedWindow,
          /*
           * Nothing was measured, so nothing may claim a currency. `null` here
           * is the same "the server did not say" this field means everywhere —
           * echoing the REQUESTED currency would label an error state with a
           * unit no figure was ever computed in. The same holds for the scope
           * beside it.
           */
          measuredInCurrency: null,
          measuredForBillingRegion: null,
          fxMode: 'document',
          translation: null,
          actualsQuery: null,
          error: refused ? null : caught instanceof Error ? caught.message : 'Could not read these figures.',
          refused: refused ? (caught as ApiError).message : null,
        });
      });

    return () => controller.abort();
  }, [
    enabled,
    key,
    wKey,
    askedName,
    askedFrom,
    askedTo,
    askedPeriodId,
    subjectType,
    subjectId,
    subjectGroupId,
    baseCurrency,
    billingRegion,
    fxMode,
  ]);

  if (!enabled || window === null) return NO_ACTUALS;
  const current = state !== null && state.key === key ? state : null;
  if (current === null) return { ...NO_ACTUALS, window, loading: true };

  return {
    byMetric: current.byMetric,
    attribution: current.attribution,
    unattributed: current.unattributed,
    window: current.window,
    measuredInCurrency: current.measuredInCurrency,
    measuredForBillingRegion: current.measuredForBillingRegion,
    fxMode: current.fxMode,
    translation: current.translation,
    actualsQuery: current.actualsQuery,
    loading: false,
    error: current.error,
    refused: current.refused,
  };
}

/* -------------------------------------------------------------------------
 * The teams on offer, and who is in one
 * --------------------------------------------------------------------- */

export interface UserGroupsState {
  groups: UserGroup[];
  loading: boolean;
  error: string | null;
}

/**
 * The groups a team can be chosen from.
 *
 * ⚠️ **`isActive: true`, opt-in with no default**, like every other reference
 * master here: omit it and retired groups come back too. **UNPAGINATED** — the
 * response is `{data: [...]}` with no `meta`, so there is no pager to write.
 *
 * ⚠️ **Every group is offered, including the ones that cannot attribute.**
 * `delivery_lead` and `approvers` produce `actual: null` and a server-composed
 * sentence saying why, which is a real and useful answer — hiding them would
 * make a target somebody deliberately set invisible, and this app would then be
 * holding its own copy of the server's `RELATIONSHIP_BY_GROUP_SLUG` in a place
 * nothing keeps in step.
 */
export function useUserGroups(): UserGroupsState {
  const [state, setState] = useState<{ groups: UserGroup[]; error: string | null } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listUserGroups({ isActive: true }, signal)
      .then((answer) => {
        if (signal.aborted) return;
        setState({ groups: answer.data, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setState({
          groups: [],
          error: caught instanceof Error ? caught.message : 'Could not read the teams.',
        });
      });

    return () => controller.abort();
  }, []);

  if (state === null) return { groups: [], loading: true, error: null };
  return { groups: state.groups, loading: false, error: state.error };
}

export interface TeamMembersState {
  members: User[];
  loading: boolean;
  error: string | null;
  /**
   * More members exist than were read.
   *
   * ⚠️ **Reported rather than swallowed.** `/users` caps `perPage` at 100 and a
   * breakdown missing its 101st person is indistinguishable from that person
   * having contributed nothing — which on this page is a statement about
   * somebody's performance.
   */
  hasMore: boolean;
}

/**
 * Who is in the team.
 *
 * ⚠️ **Membership is read LIVE and so is the server's**, which is what makes the
 * two agree. The owner took that knowingly on 2026-08-31: somebody joining the
 * account-lead group brings every document they lead with them, including
 * documents in a window already reported on. A membership snapshot is a table
 * nobody has asked for.
 */
export function useTeamMembers(subject: TeamSubject): TeamMembersState {
  const [state, setState] = useState<{
    key: string;
    members: User[];
    error: string | null;
    hasMore: boolean;
  } | null>(null);

  const key = teamSubjectKey(subject);
  const groupId = subject.kind === 'group' ? subject.groupId : null;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    /*
     * The organisation's "members" are every user, because the organisation
     * figure is unnarrowed and every account lead in the business contributes to
     * it. A group's are that group's.
     */
    const params = groupId === null ? { perPage: 100 } : { groupId, perPage: 100 };

    api
      .listUsers(params, signal)
      .then((page) => {
        if (signal.aborted) return;
        setState({
          key,
          members: page.data,
          error: null,
          hasMore: page.meta.total > page.data.length,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setState({
          key,
          members: [],
          error: caught instanceof Error ? caught.message : 'Could not read the team members.',
          hasMore: false,
        });
      });

    return () => controller.abort();
  }, [key, groupId]);

  const current = state !== null && state.key === key ? state : null;
  if (current === null) return { members: [], loading: true, error: null, hasMore: false };
  return {
    members: current.members,
    loading: false,
    error: current.error,
    hasMore: current.hasMore,
  };
}

/* -------------------------------------------------------------------------
 * Every member's figures, over one window
 * --------------------------------------------------------------------- */

export interface MemberFigures {
  byUser: Record<number, { byMetric: Partial<Record<TargetMetric, TargetActual>> }>;
  /** Per user: `'ok' | 'refused' | 'error'`. Absent while in flight. */
  statusByUser: Record<number, 'ok' | 'refused' | 'error'>;
  messageByUser: Record<number, string | null>;
  /**
   * Per user: the exact `/targets/actuals` query that member's figures were
   * measured with. Set only on success (`statusByUser[id] === 'ok'`). The
   * member drill-down (owner 2026-10-28) reuses it with `metric` added, so the
   * rows reconcile to the figure clicked — the twin of `ActualsState.actualsQuery`.
   */
  queryByUser: Record<number, TargetActualsParams>;
  loading: boolean;
  /**
   * The audit refusal, worded once.
   *
   * ⚠️ **Set only when EVERY read of somebody else was refused**, which is the
   * shape a missing audit grant actually has — the caller's own row succeeds. A
   * per-row refusal is on `statusByUser` and both are rendered: the banner says
   * why the column is empty, the rows say which ones.
   */
  refused: string | null;
}

/**
 * One request per member, fired together under one controller.
 *
 * ⚠️ **`Promise.allSettled`, so one refusal does not lose the others.** The
 * caller's OWN row succeeds even with no audit grant, and a `Promise.all` would
 * throw that away along with the rest.
 *
 * ⚠️ **N requests, and that is the honest cost of a per-person breakdown** —
 * there is no bulk actuals endpoint and `/targets/actuals` takes exactly one
 * subject. At twelve users that is twelve requests, which is why they are fired
 * once per (team, window) and not once per render. **If the staff list grows
 * past ~40 this needs a batched endpoint rather than a bigger loop.**
 *
 * ⚠️ *SUPERSEDED: "against a 120-per-60s per-IP budget shared across the
 * office."* It is **12,000/60s** (corrected 2026-09-17). The fan-out is still
 * the wrong shape — N round trips for one table, N failure modes, and a
 * latency floor set by the slowest — but ⛔ **the ~40 threshold above was
 * derived from the old budget and is no longer derived from anything.** Treat
 * it as a rough limit somebody should re-take against latency, not as a
 * measurement.
 */
export function useMemberFigures(
  members: readonly User[],
  window: TargetWindow | null,
  enabled: boolean,
  /** See the note on `useTargetActuals`. It joins `key` for the same reason. */
  baseCurrency?: string,
  /**
   * See the note on `useTargetActuals`. It joins `key` for the same reason, and
   * here the reason is sharper: a per-person breakdown that was narrowed to one
   * region and a breakdown that was not are the same table of the same names
   * with different numbers in it, and nothing in a row says which it is.
   *
   * ⚠️ **IT DOES NOT MULTIPLY THE FAN-OUT.** This is one more parameter on the
   * requests that already go out — one per member, fired together — not a
   * second pass per region: changing the region re-fires the same N, once.
   * ⚠️ *SUPERSEDED: "The budget is 120 per 60s per IP shared across the
   * office."* It is **12,000/60s** (corrected 2026-09-17). The point stands
   * without it — this parameter does not widen the fan-out.
   *
   * ⚠️ **`null` and `undefined` both mean the whole population**, folded at the
   * query builder. ⛔ **Neither is `'none'`.**
   */
  billingRegion?: TargetBillingRegion | null,
  /** See the note on `useTargetActuals` (owner 2026-10-28). It joins `key`. */
  fxMode: TargetFxMode = 'document',
): MemberFigures {
  const [state, setState] = useState<{
    key: string;
    byUser: Record<number, { byMetric: Partial<Record<TargetMetric, TargetActual>> }>;
    statusByUser: Record<number, 'ok' | 'refused' | 'error'>;
    messageByUser: Record<number, string | null>;
    queryByUser: Record<number, TargetActualsParams>;
    refused: string | null;
  } | null>(null);

  const ids = members.map((m) => m.id);
  const idsKey = ids.join(',');
  const wKey = windowKey(window);
  /*
   * ⚠️ **THE REGION IS THE FOURTH SEGMENT, AND OMITTING IT WOULD STRAND A WHOLE
   * TABLE.** Members, window and currency all stand still across a region
   * change, so without this the cached per-person figures survive it entirely —
   * every name right, every number scoped to something else, and no row
   * carrying anything that would betray it.
   *
   * ⚠️ **`undefined` and `'none'` are different keys**, for the reason given at
   * `useTargetActuals`: one asks for everything, the other for the companies
   * nobody has banded.
   */
  const key = `${idsKey}#${wKey}#${baseCurrency ?? ''}#${billingRegion ?? ''}#${fxMode}`;
  const askedFrom = window?.from ?? '';
  const askedTo = window?.to ?? '';
  const askedPeriodId = window?.periodId ?? null;

  useEffect(() => {
    if (!enabled || wKey === '' || idsKey === '') return;
    const controller = new AbortController();
    const { signal } = controller;

    const windowQuery =
      askedPeriodId === null ? { from: askedFrom, to: askedTo } : { periodId: askedPeriodId };
    /*
     * ⚠️ **Annotated, and built ONCE outside the loop.** The annotation is what
     * checks the key against the interface at all — the per-member object below
     * ends in a cast, which switches excess-property checking off, and a
     * conditional spread is exempt from it regardless. Hoisting it also keeps
     * every one of the N requests provably identical in scope: a region resolved
     * inside the loop is a scope that could differ per member, which is a table
     * of people measured over different populations.
     */
    const regionQuery: TargetActualsRegion =
      billingRegion === undefined || billingRegion === null ? {} : { billingRegion };
    const userIds = idsKey.split(',').map(Number);
    /*
     * One query per member, built once and kept: the request sends it and, on
     * success, `queryByUser` exposes the very same object for the drill-down.
     */
    const queries = userIds.map(
      (id) =>
        ({
          subjectType: 'user',
          subjectId: id,
          ...windowQuery,
          // Absent rather than undefined — see the note in useTargetActuals.
          ...(baseCurrency === undefined ? {} : { baseCurrency }),
          ...regionQuery,
          ...(fxMode === 'document' ? {} : { fxMode }),
        }) as TargetActualsParams,
    );

    Promise.allSettled(queries.map((query) => api.targetActuals(query, signal)))
      .then((results) => {
        if (signal.aborted) return;
        const byUser: Record<number, { byMetric: Partial<Record<TargetMetric, TargetActual>> }> = {};
        const statusByUser: Record<number, 'ok' | 'refused' | 'error'> = {};
        const messageByUser: Record<number, string | null> = {};
        const queryByUser: Record<number, TargetActualsParams> = {};
        let refusedCount = 0;
        let refusedMessage: string | null = null;

        results.forEach((result, index) => {
          const id = userIds[index];
          const query = queries[index];
          if (id === undefined || query === undefined) return;
          if (result.status === 'fulfilled') {
            const byMetric: Partial<Record<TargetMetric, TargetActual>> = {};
            for (const row of result.value.data.metrics) byMetric[row.metric] = row;
            byUser[id] = { byMetric };
            statusByUser[id] = 'ok';
            messageByUser[id] = null;
            queryByUser[id] = query;
            return;
          }
          const caught: unknown = result.reason;
          const refused = caught instanceof ApiError && caught.status === 403;
          statusByUser[id] = refused ? 'refused' : 'error';
          messageByUser[id] =
            caught instanceof Error ? caught.message : 'Could not read these figures.';
          if (refused) {
            refusedCount += 1;
            refusedMessage = caught instanceof Error ? caught.message : null;
          }
        });

        setState({
          key,
          byUser,
          statusByUser,
          messageByUser,
          queryByUser,
          /*
           * ⚠️ **Worded once only when every OTHER person was refused.** With a
           * team of one — yourself — nothing is refused and there is nothing to
           * say; with a team of twelve and no audit grant, eleven are refused and
           * the banner explains the empty column rather than repeating itself
           * eleven times.
           */
          refused: refusedCount > 0 && refusedCount >= userIds.length - 1 ? refusedMessage : null,
        });
      })
      .catch(() => {
        /*
         * `allSettled` never rejects, so this is unreachable; it exists so an
         * abort cannot surface as an unhandled rejection if that ever changes.
         */
        if (signal.aborted) return;
      });

    return () => controller.abort();
  }, [enabled, key, wKey, idsKey, askedFrom, askedTo, askedPeriodId, baseCurrency, billingRegion, fxMode]);

  if (!enabled || window === null || ids.length === 0) {
    return {
      byUser: {},
      statusByUser: {},
      messageByUser: {},
      queryByUser: {},
      loading: false,
      refused: null,
    };
  }
  const current = state !== null && state.key === key ? state : null;
  if (current === null) {
    return {
      byUser: {},
      statusByUser: {},
      messageByUser: {},
      queryByUser: {},
      loading: true,
      refused: null,
    };
  }
  return {
    byUser: current.byUser,
    statusByUser: current.statusByUser,
    messageByUser: current.messageByUser,
    queryByUser: current.queryByUser,
    loading: false,
    refused: current.refused,
  };
}

/** The subject half of an actuals query, from a team. One place, no guessing. */
export function teamActualsSubject(subject: TeamSubject): ActualsSubject {
  return subject.kind === 'organisation'
    ? { subjectType: 'organisation' }
    : { subjectType: 'user_group', subjectGroupId: subject.groupId };
}
